/**
 * Which files and spawn codes two resources are fighting over, and which copy
 * wins today.
 *
 * The collision detection itself is not here and must not be. `streamAssets.ts`
 * already folds every resource's files into the one flat namespace FiveM streams
 * into, `analyzeResourceSet` already runs it across a whole drop, and
 * `vehicleMeta.ts` already reads `vehicles.meta` with a real XML parser. This
 * module is the layer above those: it takes the contests they found and adds the
 * three answers a server owner actually came for, none of which a name-only pass
 * can produce.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE THREE THINGS THIS ADDS, AND THE DEFECT EACH ONE CLOSES
 *
 * 1. WHICH COPY WINS TODAY. `parseServerCfg` was built in Phase 2 for exactly
 *    this consumer, and `ParsedCfg.order` is start order in file order — the
 *    thing that actually decides the question. The prior art parses the real
 *    order into a field, never reads the field again, sorts the copies
 *    alphabetically and calls the last one the winner. Its single headline
 *    promise is answered with a guess.
 *
 *    So the rule here is absolute and is worth stating as a rule rather than as
 *    an implementation detail: `ClashWinner.resolved` is false whenever
 *    `options.cfg` is absent. There is no code path in this file that produces a
 *    named winner without a config to justify it. Where the config exists but
 *    cannot settle it — a resource it never starts, a bracket category whose
 *    internal order is the folder's, two resources that share the one name an
 *    `ensure` line can address — the answer is still "load order unknown", with
 *    the sentence that says what would settle it.
 *
 * 2. IDENTICAL OR DIFFERENT. Two resources shipping one name always collide;
 *    whether that matters depends entirely on whether the two files are the same
 *    bytes. A vendor shipping one shared wheel from two of their own packs is
 *    harmless and loses nothing in game. A genuine override is the finding worth
 *    acting on. Theirs paints both red, with a copy count, while holding both
 *    files.
 *
 *    Names first, bytes second, and only for the files that actually collide:
 *    `clashCandidates` is the cheap name-only pass that says which files are
 *    worth reading, `hashClashFiles` is the read, and `findClashes` is pure and
 *    takes the *answers*. That split is the shape the rest of this layer uses —
 *    see `GtaResourceInput.identicalPairs`, whose own comment says the caller
 *    decides whether to spend the read — and it is what keeps a 20 MB model out
 *    of the analyzer's hands.
 *
 * 3. THE DECLARATIONS, NOT JUST THE FILE NAMES. `manifest.data_file.*` lines are
 *    the other half of the namespace two resources can fight over, and a
 *    filename-only check cannot see any of it. See the long argument below, at
 *    `dataFileClashes`, for why this fires on content keys and global names
 *    rather than on the bare fact that two packs both wrote
 *    `data_file 'HANDLING_FILE' 'handling.meta'` — which is what every vehicle
 *    pack on earth writes and is not a fault.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THE `data_file` RULE IS HERE AND NOT IN `analyze.ts`
 *
 * `analyzeResourceSet` holds every manifest and could host it, and that was the
 * first instinct. Two things argue against it.
 *
 * The rule needs the *parsed contents* of the registered metas of two different
 * resources at once, compared key by key. `analyzeResourceSet` deliberately does
 * no cross-resource content work — its cross-resource block is a namespace fold
 * and a dependency graph, and adding a second content pass there makes every
 * consumer of it pay for a question only this tool asks.
 *
 * And `analyzeResourceSet` is the engine under four shipped surfaces. An
 * error-severity rule added there changes what PackBench blocks an export on and
 * what the desktop report shows, for a question neither of them asked. A rule
 * that belongs to one tool lives with that tool until a second tool needs it.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE ASYMMETRY IN "WHO WINS", WHICH IS REAL AND IS NOT A BUG HERE
 *
 * A streamed asset and a vehicle archetype resolve a collision at opposite ends
 * of the start order, and a tool that applied one rule to both would be
 * confidently wrong about half of its own findings.
 *
 *   stream — the later registration replaces the earlier one in the streaming
 *   index, so the resource that starts LAST is the copy in game.
 *
 *   spawn code — the archetype is registered once per name and a second
 *   registration of a name the game already knows is discarded, so the resource
 *   that starts FIRST is the copy in game. `spawnIndex.ts` has said exactly this
 *   since Phase 2 — "whichever resource loads second is discarded" — and this
 *   module agreeing with it is not a coincidence, it is the point of having one
 *   knowledge layer.
 *
 *   data_file — no winner is named, ever, and that is a decision rather than an
 *   omission. The start order is computed and reported, because it is a fact.
 *   Which of two merged table entries the engine keeps is not one this repo has
 *   verified per `data_file` type, and inventing a rule for it would be the
 *   competitor's defect with our name on it. `ClashWinner.reason` says so.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * SCOPE — what this deliberately does not do
 *
 * It does not detect collisions. Everything it reports as contested came out of
 * `collectStreamAssets`, `analyzeVehicleMeta` or a parsed manifest, and a bug in
 * what counts as a collision is a bug in one of those, fixed once.
 *
 * It does not read a byte. `hashClashFiles` is a helper for the caller to run
 * and hands back answers; the analyzer never sees a file.
 *
 * It does not rename anything. A rename plan is Spawn Rename's job and this
 * module produces the input for it, not the edit.
 *
 * Ped model names from `peds.meta` and weapon archetypes are not read. They are
 * a real part of this namespace and are a later addition — the shapes here take
 * a `ClashKind` rather than a boolean so that adding one does not change them.
 *
 * Nothing here fetches, uploads or touches a disk.
 */

import { analyzeResourceSet, type GtaResourceSetReport } from "./analyze";
import { META_DATA_FILE_KINDS, isManifestDeclaredExtension } from "./data/dataFileKinds.v1";
import { isEmergencyVehicleName } from "./data/emergencyVehicles.v1";
import { dedupeFindings, sortFindings } from "./findings";
import { disambiguateLabels, PROSE_LABEL_STYLE } from "./labels";
import { entriesOfKind, isGlobPattern, manifestPatternToRegExp } from "./manifest";
import { baseName, extensionOf, normalizeName, normalizePath, pathKey, stemOf, stripHighLod } from "./normalize";
import { collectStreamAssets } from "./streamAssets";
import {
  parseCarcolsMeta,
  parseCarvariationsMeta,
  parseHandlingMeta,
  parseMetaXml,
} from "./vehicleMeta";
import type { ParsedCfg } from "./serverCfg";
import type {
  GtaFinding,
  GtaFindingTag,
  GtaResourceInput,
  StreamAsset,
  VanillaAssetNameIndex,
} from "./types";

// ─── Limits ──────────────────────────────────────────────────────────────────

/**
 * Contests carried into the report.
 *
 * A whole-server drop with a badly duplicated `resources/` tree can produce tens
 * of thousands, and every one of them renders as a card. The cap says so in
 * `skipped` rather than trimming in silence — the whole argument of this layer
 * is that a report which stops looking and prints a clean footer is worse than
 * no report.
 */
const MAX_CLASHES = 5000;

/**
 * Copies named inside one contest's sentence before it summarises.
 *
 * Matches `streamAssets.ts`, which writes the sentence this one replaces. Twelve
 * resource names is not a paragraph.
 */
const MAX_COPIES_NAMED = 6;

/** Files `hashClashFiles` will read in one pass, before it says it stopped. */
const MAX_HASHED_FILES = 500;

/**
 * Bytes `hashClashFiles` will read in one pass.
 *
 * Two 10 MB models is a cheap comparison; four hundred of them is a frozen tab.
 * The cap is generous because the candidate list is already only the files that
 * collide, and it reports itself when it bites.
 */
const MAX_HASHED_BYTES = 512 * 1024 * 1024;

// ─── The words the surface and the tests share ───────────────────────────────

export const CLASH_FINDER_TOOL = "The Workshop — Clash Finder";

/** The headline for a contest no config was supplied to settle. */
export const LOAD_ORDER_UNKNOWN = "Load order unknown";

/**
 * What this tool did not look at, said once, plainly.
 *
 * Exported because it belongs in the export as well as on the page, for the
 * reason `SPAWN_INDEX_SCOPE_NOTE` gives: a short findings list read without it
 * is a clean bill of health nobody issued.
 */
export const CLASH_SCOPE_NOTE =
  "Clash Finder reports collisions in the streamed asset namespace, in vehicle spawn codes and in " +
  "data_file declarations. Ped models, weapon archetypes and script-level conflicts are not " +
  "checked. Everything runs in this browser tab; no file is uploaded.";

const NO_CFG_REASON =
  `${LOAD_ORDER_UNKNOWN}. No server.cfg was supplied, so nothing here knows the order these ` +
  "resources start in — and start order is the only thing that decides which copy the game keeps. " +
  "Add the server.cfg that starts them and this becomes a named answer.";

const NO_CFG_SKIP =
  "Which copy wins: no server.cfg was supplied, so no collision in this report names a winner. " +
  "Every collision below is real either way; what is missing is which half of it is live.";

// ─── Public shapes ───────────────────────────────────────────────────────────

/**
 * What kind of namespace the contest is in.
 *
 * Not a boolean and not three separate report sections, because the three
 * resolve differently — see the header — and a consumer has to be able to ask
 * which rule applied to a row it is rendering.
 */
export type ClashKind = "stream" | "spawn" | "data_file";

/** One resource's copy of a contested name. */
export type ClashCopy = {
  /** The resource's own name, allowed to repeat: two packs called `police` is the point. */
  resource: string;
  /** What separates it from the one beside it — `GtaResourceInput.identity`. */
  identity: string;
  /** The spelling to print, widened only as far as it has to be. See `labels.ts`. */
  label: string;
  /** The archive or pick it came from, or `null` when unknown. Never invented. */
  origin: string | null;
  /** Resource-relative path of the file or the declaration's value. */
  path: string;
  /** Inventory size in bytes. `0` means unknown, never "empty file". */
  size: number;
  /**
   * Where this resource sits in the config's start order, or `null`.
   *
   * `null` covers three different states on purpose — no config, a config that
   * does not start it, and a config that starts it inside a bracket category —
   * because the only thing a *row* can do with any of them is print nothing.
   * Which of the three it was is in `ClashWinner.reason`, where a sentence fits.
   */
  startPosition: number | null;
};

/** Whether the copies are the same file. `unknown` is a first-class answer. */
export type ClashVerdict = "identical" | "different" | "unknown";

/**
 * How the verdict was reached, so a reader can weigh it.
 *
 * `size` proves `different` and can never prove `identical`: two files of
 * different lengths cannot be the same bytes, and two of the same length very
 * often are not. That asymmetry is why `hash` exists and why `caller` — a
 * comparison the caller made and recorded in `GtaResourceInput.identicalPairs` —
 * is accepted for the identical direction.
 */
export type ClashEvidence = "hash" | "size" | "caller" | "none";

export type ClashContent = {
  verdict: ClashVerdict;
  evidence: ClashEvidence;
  /** One sentence a reader can argue with. Never empty, including when unknown. */
  reason: string;
};

export type ClashWinner = {
  /** True only when a `server.cfg` was supplied and settles it. See the header. */
  resolved: boolean;
  /** The winning copy's identity, or `null`. */
  identity: string | null;
  /** The winning copy's label, for a sentence. `null` when unresolved. */
  label: string | null;
  /** Why this one, or what would settle it. Never empty. */
  reason: string;
  /**
   * Things that stay true of a resolved answer.
   *
   * A config that `exec`s another file has an order this tool cannot see —
   * `serverCfg.ts` says so at the top and says a consumer has to carry the
   * caveat. So a winner is still named and the caveat travels with it, rather
   * than the answer being withheld from every server whose config is split
   * across files, which is most of them.
   */
  caveats: string[];
};

export type Clash = {
  kind: ClashKind;
  /** The contested name, case-folded: `ws_wheel_hre.ytd`, `copcar`, `audio:dlcambo_game`. */
  key: string;
  /** The same thing spelled for a heading. */
  title: string;
  copies: ClashCopy[];
  content: ClashContent;
  winner: ClashWinner;
  /** True when the base game owns this name too — context, not a fault. */
  vanilla: boolean;
  /** True when the name is police, fire, EMS or recovery fleet. */
  emergency: boolean;
  /** The finding this contest produces; the same object `findings` carries. */
  finding: GtaFinding;
};

/** One file worth reading, produced by the name-only pass. */
export type ClashCandidate = {
  /** The contested name it belongs to, lower-cased with its extension. */
  basename: string;
  /** The resource's identity — the key a digest is matched back on. */
  identity: string;
  /** The resource's own name, for a message. */
  resource: string;
  /** Resource-relative path, `/`-separated. */
  path: string;
  size: number;
};

/**
 * One answer to "what are this file's bytes", as the caller measured it.
 *
 * `digest` is `null` when the file could not be read or could not be hashed, and
 * that is not the same as absent: absent means nobody looked, `null` means
 * somebody looked and failed, and the contest says so either way rather than
 * calling the copies different.
 */
export type ClashDigest = {
  identity: string;
  path: string;
  /** Lower-case hex of a SHA-256 over the file's bytes, or `null`. */
  digest: string | null;
};

export type ClashCfgState = {
  supplied: boolean;
  /** The resources the config starts, in file order, exactly as written. */
  order: string[];
  /** What keeps this config from settling a question outright. Empty when nothing does. */
  caveats: string[];
};

export type ClashOptions = {
  /**
   * The parsed `server.cfg`, when the user supplied one.
   *
   * Absent is a supported, normal state and is the difference between "this one
   * wins" and "load order unknown". It is never filled in with a guess.
   */
  cfg?: ParsedCfg | null;
  /** The stock asset name index, when the caller loaded it. Absent means skipped, not passed. */
  vanillaNames?: VanillaAssetNameIndex;
  /** The answers from `hashClashFiles`, when the caller chose to spend the read. */
  digests?: readonly ClashDigest[];
  /** `hashClashFiles`'s own `skipped` lines, so they reach the report's footer. */
  hashNotes?: readonly string[];
};

export type ClashReport = {
  clashes: Clash[];
  /**
   * Duplicate names inside ONE resource, kept apart on purpose.
   *
   * FiveM does complain about these and they are worth fixing, but they are
   * housekeeping rather than a fight between packs, and the prior art gives them
   * the same red badge and the subtitle "across resources", which is not what
   * happened. These are the same finding objects `findings` carries — a view,
   * not a copy — so a surface can render them under their own heading without
   * the export losing them.
   */
  housekeeping: GtaFinding[];
  /** Every finding, ranked. The list to render through `FindingsList`. */
  findings: GtaFinding[];
  /**
   * The underlying set report, with `findings` replaced by the list above.
   *
   * Returned rather than kept private so the page can hand it straight to
   * `report.ts` — one export path, and a paste that cannot disagree with the
   * panel it was copied from.
   */
  report: GtaResourceSetReport;
  cfg: ClashCfgState;
  counts: {
    resources: number;
    clashes: number;
    stream: number;
    spawn: number;
    dataFile: number;
    identical: number;
    different: number;
    /** Contests of every kind with no verdict, including the ones bytes cannot settle. */
    unknownContent: number;
    /**
     * Stream contests with no verdict — the only ones a byte pass could settle.
     *
     * Separate from `unknownContent` because a surface reporting on the byte
     * pass has to count only what that pass applies to. A spawn code and a
     * `data_file` registration are `unknown` by construction — bytes are
     * irrelevant to both, and both say so in their own `content.reason` — so
     * counting them as "not compared" tells a reader whose comparison completed
     * in full that five of six things went unread. The `skipped` note below
     * already filters on `kind === "stream"`; this is the same filter, exported,
     * so the page and the report footer cannot disagree.
     */
    unknownStream: number;
    resolvedWinners: number;
  };
  skipped: string[];
};

// ─── Ordering ────────────────────────────────────────────────────────────────

/**
 * Ordering that does not depend on the machine, for the reason
 * `streamAssets.ts` and `spawnIndex.ts` both give: `localeCompare` reads the
 * host's ICU data, so the same drop scanned in two places produces two orderings
 * of one list and a saved export shows a diff nobody made.
 */
function compareText(a: string, b: string): number {
  if (a === b) return 0;
  return a < b ? -1 : 1;
}

// ─── Pass one: the names ─────────────────────────────────────────────────────

/**
 * The files worth reading, and nothing else.
 *
 * Deliberately cheap: it folds the namespace and stops. No manifest is parsed,
 * no `.meta` is read, no vehicle is cross-checked — a caller runs this to decide
 * what to spend a read on, and making that decision cost a full analysis would
 * defeat the split it exists to make.
 *
 * Only contested names come back, and only the copies of them, which is the
 * whole economy of the two-pass design: a 300-resource drop yields a handful of
 * files rather than a gigabyte.
 */
export function clashCandidates(inputs: readonly GtaResourceInput[]): ClashCandidate[] {
  const assets = collectStreamAssets(
    (inputs ?? []).filter(Boolean).map((input) => ({
      name: input.name,
      identity: input.identity,
      origin: input.origin,
      files: input.files,
    })),
  );

  const candidates: ClashCandidate[] = [];
  for (const asset of assets) {
    if (distinctIdentities(asset).size < 2) continue;
    for (const occurrence of asset.occurrences) {
      candidates.push({
        basename: asset.basename,
        identity: occurrence.resourceIdentity ?? occurrence.resource,
        resource: occurrence.resource,
        path: occurrence.path,
        size: occurrence.size,
      });
    }
  }
  return candidates;
}

function distinctIdentities(asset: StreamAsset): Set<string> {
  const out = new Set<string>();
  for (const occurrence of asset.occurrences ?? []) {
    if (!occurrence || typeof occurrence.path !== "string" || occurrence.path === "") continue;
    out.add(normalizeName(occurrence.resourceIdentity ?? occurrence.resource ?? ""));
  }
  return out;
}

// ─── Pass two: the bytes ─────────────────────────────────────────────────────

const NO_SUBTLE_CRYPTO_SKIP =
  "Identical-or-different check: this browser exposes no crypto.subtle.digest, which is what a " +
  "page served over plain HTTP or from a file:// URL looks like. Nothing was hashed, so every " +
  "contested name below says the byte comparison did not run rather than claiming the copies " +
  "differ. Open the page over HTTPS to get the split.";

/**
 * Hash the contested files, and say plainly when it could not.
 *
 * The caller supplies the read, because only the caller knows whether a copy is
 * a `File` from a pick, an entry inside a zip it still has in memory, or a path
 * over an IPC bridge. What comes back is the shape `findClashes` asks for, so
 * the analyzer stays pure and never holds twenty megabytes of model.
 *
 * The failure mode this function exists to prevent is the one the brief names:
 * `crypto.subtle` is async and is simply absent on an insecure origin, and the
 * obvious handling — treat an unhashable file as unequal — reports every vendor
 * pack's shared parts as a real override. So an absent `subtle` returns no
 * digests at all and one sentence, and a file that would not read comes back
 * with a `null` digest rather than being dropped, because "we looked and failed"
 * and "nobody looked" are different things the contest words differently.
 */
export async function hashClashFiles(
  candidates: readonly ClashCandidate[],
  read: (
    candidate: ClashCandidate,
  ) => Promise<ArrayBuffer | Uint8Array | null> | ArrayBuffer | Uint8Array | null,
  options?: { signal?: AbortSignal; maxFiles?: number; maxBytes?: number },
): Promise<{ digests: ClashDigest[]; skipped: string[] }> {
  const skipped: string[] = [];
  const digests: ClashDigest[] = [];
  const all = (candidates ?? []).filter(Boolean);

  const subtle = globalThis.crypto?.subtle;
  if (!subtle || typeof subtle.digest !== "function") {
    return { digests, skipped: [NO_SUBTLE_CRYPTO_SKIP] };
  }

  const maxFiles = positiveOr(options?.maxFiles, MAX_HASHED_FILES);
  const maxBytes = positiveOr(options?.maxBytes, MAX_HASHED_BYTES);

  let bytesRead = 0;
  let index = 0;
  for (const candidate of all) {
    if (options?.signal?.aborted) {
      skipped.push(
        `Identical-or-different check: the scan was cancelled after ${index} of ${all.length} ` +
          "contested files, so the rest say the byte comparison did not run.",
      );
      break;
    }
    if (index >= maxFiles) {
      skipped.push(
        `Identical-or-different check: only the first ${maxFiles.toLocaleString("en-US")} of ` +
          `${all.length.toLocaleString("en-US")} contested files were hashed. The rest are still ` +
          "reported as collisions; what is missing is whether their copies match.",
      );
      break;
    }
    if (bytesRead >= maxBytes) {
      skipped.push(
        `Identical-or-different check: stopped after ${formatBytes(bytesRead)} of contested files, ` +
          `at the ${formatBytes(maxBytes)} ceiling. The remaining names are still reported as ` +
          "collisions, without the identical-or-different split.",
      );
      break;
    }
    index += 1;

    let bytes: ArrayBuffer | Uint8Array | null = null;
    try {
      bytes = await read(candidate);
    } catch {
      bytes = null;
    }
    if (!bytes) {
      digests.push({ identity: candidate.identity, path: candidate.path, digest: null });
      continue;
    }

    try {
      // Copied into a plain `ArrayBuffer` rather than handed over as-is. A
      // `Uint8Array` may be a view over a `SharedArrayBuffer` — which
      // `crypto.subtle.digest` refuses outright — or a window onto part of a
      // larger buffer, and the caller's read is free to return either. One
      // allocation per contested file buys a call that cannot throw on the
      // shape of its argument, and the candidate list is already only the
      // handful of files that actually collide.
      const source = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
      const buffer = new ArrayBuffer(source.byteLength);
      new Uint8Array(buffer).set(source);
      bytesRead += buffer.byteLength;
      const hashed = await subtle.digest("SHA-256", buffer);
      digests.push({
        identity: candidate.identity,
        path: candidate.path,
        digest: toHexDigest(hashed),
      });
    } catch {
      digests.push({ identity: candidate.identity, path: candidate.path, digest: null });
    }
  }

  return { digests, skipped };
}

function positiveOr(value: number | undefined, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) && value > 0 ? value : fallback;
}

function toHexDigest(buffer: ArrayBuffer): string {
  let out = "";
  for (const byte of new Uint8Array(buffer)) out += byte.toString(16).padStart(2, "0");
  return out;
}

/** Bytes as a person reads them. Local to this module; the report formatter is `resmon.ts`'s. */
function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value >= 100 || unit === 0 ? Math.round(value) : value.toFixed(1)} ${units[unit]}`;
}

// ─── The config, read once ───────────────────────────────────────────────────

type CfgIndex = {
  supplied: boolean;
  /**
   * Normalized resource name → the position of its FIRST `ensure`.
   *
   * The first, not the last, and that is not a detail: a second `ensure` of a
   * resource the server has already started does nothing at all, so a config
   * that repeats a line — `EXISTING_SERVER_CFG` repeats one, because real ones
   * do — has a start order in which the repeated resource still starts where it
   * first appeared. Taking the last index would move it to the end of the order
   * and hand the wrong copy the win, which is the exact class of confidently
   * wrong answer this tool exists to replace.
   */
  position: Map<string, number>;
  /** Names this config `ensure`s more than once. */
  repeated: Set<string>;
  /**
   * Names this config addresses that more than one resource in the drop carries.
   *
   * Decided over the whole drop and not over one contest, and that is the point.
   * An `ensure police` line names a resource the server resolves by name, so
   * with two folders called `police` in the drop it cannot be attributed to
   * either of them — whether or not both of them happen to appear in the
   * contest being resolved. A guard that only looked inside one contest answered
   * "load order unknown" for a collision between the two `police` packs and then
   * named a confident winner for a collision between one of them and a third
   * resource, from the same unattributable line. That is the same guess this
   * module exists to replace, produced by the module itself.
   *
   * Scoped to names the config actually addresses: two same-named packs that no
   * `ensure` line mentions create no ambiguity about an order, because neither
   * of them starts.
   */
  ambiguous: Set<string>;
  /** Bracket categories started as a unit, lower-cased, `[cars]`. */
  categories: Set<string>;
  /** How many `exec` lines point at a config this tool cannot read. */
  execs: number;
  order: string[];
  truncated: string[];
};

function indexCfg(
  cfg: ParsedCfg | null | undefined,
  resources: readonly GtaResourceInput[],
): CfgIndex {
  const position = new Map<string, number>();
  const repeated = new Set<string>();
  const ambiguous = new Set<string>();
  if (!cfg) {
    return {
      supplied: false,
      position,
      repeated,
      ambiguous,
      categories: new Set(),
      execs: 0,
      order: [],
      truncated: [],
    };
  }

  const order = Array.isArray(cfg.order) ? cfg.order.filter((name) => typeof name === "string") : [];
  order.forEach((name, index) => {
    const key = normalizeName(name);
    if (key === "") return;
    if (position.has(key)) {
      repeated.add(key);
      return;
    }
    position.set(key, index);
  });

  const held = new Map<string, number>();
  for (const input of resources) {
    const key = normalizeName(input.name);
    if (key === "") continue;
    held.set(key, (held.get(key) ?? 0) + 1);
  }
  for (const [name, count] of held) {
    if (count > 1 && position.has(name)) ambiguous.add(name);
  }

  const execs = (cfg.lines ?? []).filter((line) => line?.kind === "exec").length;
  return {
    supplied: true,
    position,
    repeated,
    ambiguous,
    categories: new Set(cfg.categories ?? []),
    execs,
    order,
    truncated: [...(cfg.truncated ?? [])],
  };
}

/** `[cars]` in `[cars]/police`, when the identity carries one. */
function categoryOf(identity: string): string | null {
  for (const segment of normalizePath(identity).split("/")) {
    if (segment.startsWith("[") && segment.endsWith("]")) return segment.toLowerCase();
  }
  return null;
}

// ─── Who wins ────────────────────────────────────────────────────────────────

/**
 * Which copy the game keeps, or why that is not answerable.
 *
 * `end` is which end of the start order wins for this kind of clash, and the
 * header argues why the two ends are both correct. `data_file` never reaches
 * here for a verdict: it is called with `null` and gets the order reported and
 * the winner withheld, on purpose.
 */
function resolveWinner(
  copies: readonly ClashCopy[],
  cfg: CfgIndex,
  end: "first" | "last" | null,
): ClashWinner {
  const caveats: string[] = [];

  if (!cfg.supplied) return { resolved: false, identity: null, label: null, reason: NO_CFG_REASON, caveats };

  if (end === null) {
    return {
      resolved: false,
      identity: null,
      label: null,
      reason:
        "The start order is known and is shown against each copy. Which of two merged data_file " +
        "registrations the engine keeps is not something this tool has verified per data_file " +
        "type, so it does not name a winner rather than guessing at one. Removing the duplicate " +
        "registration is the fix either way.",
      caveats,
    };
  }

  // A config addresses a resource by name, and two resources are allowed to
  // share one. When they do, an `ensure` line cannot be attributed to either of
  // them — and a server cannot start both, which is itself the larger problem.
  //
  // Two copies of that name inside THIS contest is the obvious case; the
  // dangerous one is a contest holding only one of them, where the other sits
  // elsewhere in the drop and the same unattributable line is quietly read as
  // this copy's start position. `cfg.ambiguous` is decided over the whole drop
  // for exactly that reason — see its comment — and both readings are checked
  // here so that a drop with no config still gets the first one.
  const byName = new Map<string, number>();
  for (const copy of copies) {
    const key = normalizeName(copy.resource);
    byName.set(key, (byName.get(key) ?? 0) + 1);
  }
  const shared = [
    ...new Set([
      ...[...byName.entries()].filter(([, count]) => count > 1).map(([name]) => name),
      ...[...byName.keys()].filter((name) => cfg.ambiguous.has(name)),
    ]),
  ].sort(compareText);
  if (shared.length > 0) {
    return {
      resolved: false,
      identity: null,
      label: null,
      reason:
        `${LOAD_ORDER_UNKNOWN}. More than one resource in this drop is called ` +
        `${shared.map((name) => `\`${name}\``).join(", ")}, and a config starts a resource by ` +
        "name — so an ensure line here cannot be attributed to one folder rather than the other, " +
        "and a server cannot start both in the first place. Rename one of the folders, then this " +
        "question has an answer.",
      caveats,
    };
  }

  const started = copies.filter((copy) => copy.startPosition !== null);
  const unstarted = copies.filter((copy) => copy.startPosition === null);

  if (started.length === 0) {
    return {
      resolved: false,
      identity: null,
      label: null,
      reason:
        `${LOAD_ORDER_UNKNOWN}. This config starts none of these resources by name. ` +
        unstartedExplanation(unstarted, cfg),
      caveats,
    };
  }

  if (unstarted.length > 0) {
    const category = unstarted.find((copy) => {
      const bracket = categoryOf(copy.identity);
      return bracket !== null && cfg.categories.has(bracket);
    });
    if (category) {
      return {
        resolved: false,
        identity: null,
        label: null,
        reason:
          `${LOAD_ORDER_UNKNOWN}. ${category.label} is started as part of ` +
          `\`${categoryOf(category.identity)}\`, and a bracket ensure starts everything in that ` +
          "folder in the folder's own order, which this file does not state. Ensure it by name to " +
          "pin it down.",
        caveats,
      };
    }
    if (cfg.execs > 0) {
      return {
        resolved: false,
        identity: null,
        label: null,
        reason:
          `${LOAD_ORDER_UNKNOWN}. ${listLabels(unstarted.map((copy) => copy.label))} ` +
          `${unstarted.length === 1 ? "is" : "are"} not started by name in this config, and it ` +
          `\`exec\`s ${cfg.execs} other file${cfg.execs === 1 ? "" : "s"} this tool cannot read — ` +
          "so it may well be started there, at a position that is not here. Supply the exec'd " +
          "config too, or ensure it by name.",
        caveats,
      };
    }
    caveats.push(
      `${listLabels(unstarted.map((copy) => copy.label))} ` +
        `${unstarted.length === 1 ? "is" : "are"} not started by this config at all, so ` +
        `${unstarted.length === 1 ? "its copy never reaches" : "those copies never reach"} the ` +
        "game. That is a result of an omission rather than of a decision, and adding the ensure " +
        "line changes this answer.",
    );
  }

  const winner = pickEnd(started, end);
  if (cfg.execs > 0) {
    caveats.push(
      `This config \`exec\`s ${cfg.execs} other file${cfg.execs === 1 ? "" : "s"}, whose own ensure ` +
        "lines are not readable from here. If one of them starts one of these resources earlier, " +
        "the real order differs from this one.",
    );
  }
  for (const copy of started) {
    if (cfg.repeated.has(normalizeName(copy.resource))) {
      caveats.push(
        `${copy.label} is ensured more than once. The first line is the one that starts it — a ` +
          "later ensure of a running resource does nothing — so the position used here is the " +
          "first occurrence.",
      );
    }
  }
  if (cfg.truncated.length > 0) caveats.push(...cfg.truncated);

  // The one-started case is worded on its own. "starts last of these, at
  // position 1" is a true sentence about a list of one and reads as nonsense to
  // the person holding the config, and the reason a reader needs here is not the
  // order at all — it is that nothing else in the contest ever starts.
  const reason =
    started.length === 1
      ? `${winner.label} is the only one of these this config starts, at position ` +
        `${(winner.startPosition ?? 0) + 1} in its ensure order, so its copy is the one in game ` +
        "today by default rather than by winning anything. Starting the others changes this " +
        "answer, which is why the collision is worth fixing now rather than after."
      : `${winner.label} starts ${end === "last" ? "last" : "first"} of these, at position ` +
        `${(winner.startPosition ?? 0) + 1} in the config's ensure order, so ${describeEnd(end)} ` +
        `${winner.label}'s copy is the one in game today. Change the ensure order and this answer ` +
        "changes with it, which is why it is worth fixing the collision rather than the order.";

  return { resolved: true, identity: winner.identity, label: winner.label, reason, caveats };
}

function describeEnd(end: "first" | "last"): string {
  return end === "last"
    ? "— the later registration replaces the earlier one in the streaming index —"
    : "— the archetype is registered once and a later duplicate is discarded —";
}

function pickEnd(copies: readonly ClashCopy[], end: "first" | "last"): ClashCopy {
  return copies.reduce((best, copy) => {
    const a = copy.startPosition ?? 0;
    const b = best.startPosition ?? 0;
    if (a === b) return best;
    return end === "last" ? (a > b ? copy : best) : a < b ? copy : best;
  });
}

function unstartedExplanation(copies: readonly ClashCopy[], cfg: CfgIndex): string {
  const bracket = copies
    .map((copy) => categoryOf(copy.identity))
    .find((name) => name !== null && cfg.categories.has(name));
  if (bracket) {
    return (
      `They sit inside \`${bracket}\`, which this config starts as a unit — a bracket ensure ` +
      "starts everything in that folder in the folder's own order, which the config does not " +
      "state. Ensure them by name to pin the order down."
    );
  }
  if (cfg.execs > 0) {
    return (
      `It \`exec\`s ${cfg.execs} other file${cfg.execs === 1 ? "" : "s"} this tool cannot read, so ` +
      "they are probably started there. Supply that config too."
    );
  }
  return (
    "Either they are started from somewhere this file does not point at, or they do not run at " +
    "all — in which case the collision is not live and neither copy is in game."
  );
}

/** `a`, `b` and `c`, or `a`, `b`, and 4 more once it stops being a sentence. */
function listLabels(labels: readonly string[]): string {
  const shown = labels.slice(0, MAX_COPIES_NAMED).map((label) => label || "an unnamed resource");
  const hidden = labels.length - shown.length;
  if (shown.length === 0) return "Several resources";
  if (hidden > 0) return `${shown.join(", ")}, and ${hidden} more`;
  if (shown.length === 1) return shown[0];
  return `${shown.slice(0, -1).join(", ")} and ${shown[shown.length - 1]}`;
}

// ─── Identical or different ──────────────────────────────────────────────────

const NOT_COMPARED =
  "The byte comparison did not run for these copies, so this report does not say whether they are " +
  "the same file. It is reported as a collision either way — the namespace is flat and one copy " +
  "still loses — but whether that matters in game depends on the answer this line does not have.";

/**
 * The verdict, from the strongest evidence available.
 *
 * The order of the tests is the argument. A pair of digests settles it outright.
 * Failing that, two different *lengths* prove the files differ without anyone
 * reading a byte, which is the cheapest true answer in the whole tool and is why
 * `collision-different/` needs no hash to be called different. Only then is the
 * caller's own recorded comparison consulted, and only for the identical
 * direction, because a claim that contradicts the inventory's own sizes is a
 * claim about files that are visibly not the same.
 *
 * And when none of the three applies, the answer is `unknown` and says so. It is
 * never `different` by default: reporting a vendor's own shared part as a real
 * override because nobody hashed it is precisely the failure the split exists to
 * fix, arrived at from the other side.
 */
function resolveContent(
  copies: readonly ClashCopy[],
  digests: Map<string, string | null>,
  claims: ClaimIndex,
): ClashContent {
  const hashes = copies.map((copy) => digests.get(copyKey(copy.identity, copy.path)));
  const known = hashes.filter((hash): hash is string => typeof hash === "string" && hash !== "");

  if (known.length >= 2 && new Set(known).size > 1) {
    return {
      verdict: "different",
      evidence: "hash",
      reason:
        "The copies hash to different values, so they are genuinely different files. One of them " +
        "is being shadowed: whatever the losing pack shipped never appears in game, and nothing " +
        "in the server console says so. This is the half of the split worth acting on.",
    };
  }
  if (known.length === copies.length && copies.length > 1 && new Set(known).size === 1) {
    return {
      verdict: "identical",
      evidence: "hash",
      reason:
        "Every copy hashes to the same value, so these are byte-for-byte the same file. One of " +
        "them still loses the namespace, and it does not matter: the game gets the same asset " +
        "either way. What it costs is the download and the streaming memory for a file shipped " +
        "twice, which is a budget question rather than a correctness one.",
    };
  }

  const sizes = copies.map((copy) => copy.size);
  if (sizes.every((size) => size > 0) && new Set(sizes).size > 1) {
    return {
      verdict: "different",
      evidence: "size",
      reason:
        `The copies are different lengths (${[...new Set(sizes)]
          .sort((a, b) => a - b)
          .map(formatBytes)
          .join(" and ")}), so they cannot be the same file — no hashing needed. One pack is ` +
        "shadowing the other and the loser's asset never appears in game.",
    };
  }

  if (copies.length > 1 && claims.allIdentical(copies)) {
    return {
      verdict: "identical",
      evidence: "caller",
      reason:
        "These were compared byte-for-byte before the analysis ran and came back the same file. " +
        "One copy still loses the namespace and it changes nothing in game — the same asset loads " +
        "either way — so this is harmless, and what it costs is shipping one file twice.",
    };
  }

  return {
    verdict: "unknown",
    evidence: "none",
    reason: hashes.some((hash) => hash === null)
      ? "One of these copies could not be read, so the byte comparison did not complete and this " +
        "report does not say whether they are the same file. It is a real collision either way."
      : NOT_COMPARED,
  };
}

/**
 * One copy's address: which resource, and which file inside it.
 *
 * Joined on NUL for the reason `collectStreamAssets` gives about its own dedupe
 * key. An identity and a path can each contain a space and a slash, so
 * `"lspd pack"` plus `"a.ydr"` must not produce the same key as `"lspd"` plus
 * `"pack a.ydr"` and quietly answer a digest lookup with another file's hash.
 * NUL is the one character neither a resource root nor a path can hold, and the
 * same join spells the claim-pair keys and the finding-replacement keys below.
 */
function copyKey(identity: string, path: string): string {
  return `${normalizeName(identity)}\u0000${pathKey(path)}`;
}

/**
 * The caller's own byte comparisons, resolved onto copies.
 *
 * `GtaResourceInput.identicalPairs` is resource-relative, and the far side of a
 * cross-resource claim is qualified with the other resource's *name* — a
 * spelling that exists nowhere else in this layer, as the fixture that produces
 * it says out loud. A name is not a resource: when two packs share one, the
 * claim cannot be attributed to either, so it is dropped rather than guessed at.
 * Dropping it costs an `unknown` verdict, which is a true answer; guessing costs
 * a wrong one.
 */
type ClaimIndex = { allIdentical(copies: readonly ClashCopy[]): boolean };

function buildClaimIndex(inputs: readonly GtaResourceInput[]): ClaimIndex {
  const byName = new Map<string, string[]>();
  for (const input of inputs) {
    const key = normalizeName(input.name);
    byName.set(key, [...(byName.get(key) ?? []), input.identity ?? input.name]);
  }

  const pairs = new Set<string>();
  for (const input of inputs) {
    const identity = input.identity ?? input.name;
    for (const pair of input.identicalPairs ?? []) {
      if (!Array.isArray(pair) || pair.length !== 2) continue;
      const near = copyKey(identity, pair[0]);
      const far = resolveFar(identity, pair[1], byName);
      if (far === null) continue;
      pairs.add(near < far ? `${near}\u0000${far}` : `${far}\u0000${near}`);
    }
  }

  return {
    allIdentical(copies) {
      for (let i = 0; i < copies.length; i += 1) {
        for (let j = i + 1; j < copies.length; j += 1) {
          const a = copyKey(copies[i].identity, copies[i].path);
          const b = copyKey(copies[j].identity, copies[j].path);
          const key = a < b ? `${a}\u0000${b}` : `${b}\u0000${a}`;
          if (!pairs.has(key)) return false;
        }
      }
      return copies.length > 1;
    },
  };
}

function resolveFar(
  identity: string,
  value: string,
  byName: ReadonlyMap<string, string[]>,
): string | null {
  if (typeof value !== "string" || value === "") return null;
  const path = normalizePath(value);
  const slash = path.indexOf("/");
  if (slash <= 0) return copyKey(identity, path);

  const head = normalizeName(path.slice(0, slash));
  const owners = byName.get(head);
  // Ambiguous, or not a resource name at all: the near resource's own relative
  // path is the only reading left, which is what a same-resource claim is.
  if (!owners || owners.length !== 1) return copyKey(identity, path);
  return copyKey(owners[0], path.slice(slash + 1));
}

// ─── data_file declarations ──────────────────────────────────────────────────

/**
 * The `data_file` types whose declared *value* is a name the whole server
 * shares, rather than a path into a table the resource owns.
 *
 * This distinction is the whole rule, and getting it wrong in either direction
 * is a shipped defect:
 *
 *   Every vehicle pack on earth writes `data_file 'HANDLING_FILE' 'handling.meta'`.
 *   Forty packs on one server write it forty times and nothing is wrong. A tool
 *   that reports that as a clash — which is the literal reading of "two
 *   resources registering the same data_file" — produces one red finding naming
 *   forty correct resources on the first server it is pointed at, and is
 *   uninstalled.
 *
 *   Two resources registering `AUDIO_GAMEDATA 'audioconfig/dlcambo_game'` really
 *   are fighting, because the audio bank is registered under that name globally
 *   and the second registration lands on the first. Nothing about the file names
 *   on disk shows it: the value is a stem, and the file is
 *   `dlcambo_game.dat151.rel`.
 *
 * So the audio types and the ityp request are keyed by their declared name, and
 * everything else is keyed by what its *contents* claim — see
 * `MERGED_TABLE_KEYS`. `isManifestDeclaredExtension` widens the first list to
 * cover `.rel`, `.dat` and `.gxt2` values under any type, which is the half of
 * the namespace `dataFileKinds.v1.ts` split out for exactly this consumer: those
 * three reach the game only because a manifest line names them, so the
 * declaration is the thing that collides.
 *
 * Both halves are then narrowed by `FIXED_CONVENTION_DATA_FILE_NAMES`, because
 * a type being global-name is not on its own enough: `AUDIO_WAVEPACK` is in the
 * list above and `data/audio_occlusion.dat` is a filename the convention fixes,
 * so without that guard the rule lands on the very failure the paragraph above
 * describes.
 */
const GLOBAL_NAME_DATA_FILE_TYPES: ReadonlySet<string> = new Set([
  "AUDIO_GAMEDATA",
  "AUDIO_SOUNDDATA",
  "AUDIO_SPEECHDATA",
  "AUDIO_SYNTHDATA",
  "AUDIO_WAVEPACK",
  "AUDIO_DYNAMIXDATA",
  "DLC_ITYP_REQUEST",
]);

/**
 * The registered files whose contents share one server-wide table, and the key
 * the engine files each row under.
 *
 * Only the three this layer already has a parser for *and* does not already
 * contest somewhere else. A type with no parser is not guessed at and not
 * reported — `KNOWN_DATA_FILE_TYPES` is explicit that the engine's list is
 * longer than this repo's, and a rule that fired on a type it could not read
 * would be asserting a collision it never checked.
 *
 * `VEHICLE_METADATA_FILE` is deliberately absent, and its absence is the rule
 * rather than an oversight. `spawnClashes` already contests exactly those rows:
 * it is built from `VehicleEntity`, which `analyzeVehicleMeta` derives from
 * every `CVehicleModelInfo__InitDataList` document in the pack, so every model
 * a `VEHICLE_METADATA_FILE` entry here could find is already a `spawn` clash.
 * Keeping it produced one duplicated spawn code as two error cards with two
 * different sentences — and, worse, two different *answers*, because a spawn
 * contest resolves at the first-started end and `data_file` names no winner at
 * all. One problem, one card, one answer.
 */
const MERGED_TABLE_KEYS: Record<
  string,
  { noun: string; keysOf: (doc: Document) => string[] }
> = {
  HANDLING_FILE: {
    noun: "handling entry",
    keysOf: (doc) => parseHandlingMeta(doc).map((entry) => entry.handlingName),
  },
  VEHICLE_VARIATION_FILE: {
    noun: "carvariations entry",
    keysOf: (doc) => parseCarvariationsMeta(doc).map((row) => row.model),
  },
  CARCOLS_FILE: {
    noun: "mod kit",
    keysOf: (doc) => parseCarcolsMeta(doc).kits.map((kit) => kit.kitName),
  },
};

type DataFileDeclaration = {
  input: GtaResourceInput;
  identity: string;
  label: string;
  type: string;
  /** The declared value, normalized. */
  value: string;
};

/** Every `data_file` line in the set, with the resource that wrote it. */
function declarations(
  inputs: readonly GtaResourceInput[],
  reports: GtaResourceSetReport,
  labels: ReadonlyMap<string, string>,
): DataFileDeclaration[] {
  const out: DataFileDeclaration[] = [];
  inputs.forEach((input, index) => {
    const manifest = reports.resources[index]?.manifest;
    if (!manifest) return;
    const identity = input.identity ?? input.name;
    const label = labels.get(normalizeName(identity)) ?? input.name;
    for (const entry of entriesOfKind(manifest, "data_file")) {
      const type = (entry.qualifier ?? "").trim().toUpperCase();
      const value = normalizePath(entry.value ?? "");
      if (type === "" || value === "" || isGlobPattern(value)) continue;
      out.push({ input, identity, label, type, value });
    }
  });
  return out;
}

/**
 * The two `data_file` clash rules, run over one set.
 *
 * Rule one is the declaration: two resources naming one global name. Rule two is
 * the contents: two resources' registered files defining the same row in one
 * shared table — two vehicle packs that both define handling for `police` is the
 * common, invisible version of this, and a filename check sees nothing because
 * both files are called `handling.meta` and both are supposed to be.
 *
 * A registered file that is in the inventory and not in `texts` was not opened,
 * and rule two cannot run on it. That goes to `skipped` in words rather than
 * counting as a pass.
 *
 * `spawnContests` is the set of model names `spawnClashes` has already contested
 * between the same resources, and it is here so that a reader of a pack that
 * ships the usual four vehicle metas is told the cards belong together. Two
 * packs both defining `police` produce a spawn contest, a handling-table contest
 * and a carvariations-table contest, and those are three separate registrations
 * needing three separate edits — so they stay three rows rather than being
 * folded into one, which would hide two of the three files that have to change.
 * What they get instead is the sentence saying they are one rename.
 */
function dataFileClashes(
  inputs: readonly GtaResourceInput[],
  reports: GtaResourceSetReport,
  labels: ReadonlyMap<string, string>,
  cfg: CfgIndex,
  skipped: string[],
  spawnContests: ReadonlyMap<string, ReadonlySet<string>>,
): Clash[] {
  const decls = declarations(inputs, reports, labels);
  const clashes: Clash[] = [];

  // ── Rule one: one global name, two declarations ──────────────────────────

  const byGlobalName = new Map<string, DataFileDeclaration[]>();
  for (const decl of decls) {
    const globalName = globalNameOf(decl);
    if (globalName === null) continue;
    const key = `${decl.type}\u0000${globalName}`;
    byGlobalName.set(key, [...(byGlobalName.get(key) ?? []), decl]);
  }

  for (const [key, group] of byGlobalName) {
    const owners = new Map<string, DataFileDeclaration>();
    for (const decl of group) {
      if (!owners.has(normalizeName(decl.identity))) owners.set(normalizeName(decl.identity), decl);
    }
    if (owners.size < 2) continue;
    const members = [...owners.values()];
    const copies = members.map((decl) =>
      copyOf(decl.input, decl.label, decl.value, 0, cfg),
    );
    const name = key.split("\u0000")[1];
    const type = members[0].type;
    clashes.push(
      finishClash({
        kind: "data_file",
        key: `${type.toLowerCase()}:${name}`,
        title: `${type} ${name}`,
        copies,
        cfg,
        content: {
          verdict: "unknown",
          evidence: "none",
          reason:
            "A global-name registration does not need a byte comparison to be a problem: two " +
            "resources claiming one name is the fault, whatever is behind it.",
        },
        vanilla: false,
        emergency: false,
        finding: {
          code: "manifest.data_file.double_registered",
          severity: "error",
          title: `${name} is registered as ${type} by ${copies.length} resources`,
          detail:
            `${listLabels(copies.map((copy) => copy.label))} each declare ` +
            `\`data_file '${type}' '${members[0].value}'\`. The value here is not a path into the ` +
            "resource — it is the name the whole server files this data under — so the two " +
            "registrations land on each other and one of them is simply lost, with nothing in the " +
            "console to say which. Nothing about the file names on disk shows this, which is why " +
            "a collision checker that only compares file names never reports it. Rename the data " +
            "in one of them, or ship it from a single resource.",
          entity: { kind: "manifest", id: `${type} ${name}` },
        },
      }),
    );
  }

  // ── Rule two: one shared table row, two registered files ─────────────────

  const unreadable: string[] = [];
  type TableRow = { decl: DataFileDeclaration; noun: string };
  const byTableKey = new Map<string, TableRow[]>();

  for (const decl of decls) {
    const table = MERGED_TABLE_KEYS[decl.type];
    if (!table) continue;
    const text = decl.input.texts.get(pathKey(decl.value));
    if (text === undefined) {
      if (decl.input.files.some((file) => pathKey(file.path) === pathKey(decl.value))) {
        unreadable.push(`${decl.label} — ${decl.value}`);
      }
      continue;
    }
    const doc = parseMetaXml(text);
    if (!doc) continue;
    for (const raw of table.keysOf(doc)) {
      const rowKey = normalizeName(raw);
      if (rowKey === "") continue;
      const key = `${decl.type}\u0000${rowKey}`;
      byTableKey.set(key, [...(byTableKey.get(key) ?? []), { decl, noun: table.noun }]);
    }
  }

  for (const [key, rows] of byTableKey) {
    const owners = new Map<string, TableRow>();
    for (const row of rows) {
      if (!owners.has(normalizeName(row.decl.identity))) {
        owners.set(normalizeName(row.decl.identity), row);
      }
    }
    if (owners.size < 2) continue;
    const members = [...owners.values()];
    const copies = members.map((row) =>
      copyOf(row.decl.input, row.decl.label, row.decl.value, 0, cfg),
    );
    const [type, rowKey] = key.split("\u0000");
    const noun = members[0].noun;
    const emergency = isEmergencyVehicleName(rowKey).emergency;
    // The same name, between the same packs, already contested as a spawn code.
    // Said in the finding rather than folded into it: the model name and this
    // table row live in two different files and both have to change.
    const spawnOwners = spawnContests.get(rowKey);
    const alsoSpawn =
      spawnOwners !== undefined &&
      copies.every((copy) => spawnOwners.has(normalizeName(copy.identity)));
    clashes.push(
      finishClash({
        kind: "data_file",
        key: `${type.toLowerCase()}:${rowKey}`,
        title: `${type} ${rowKey}`,
        copies,
        cfg,
        content: {
          verdict: "unknown",
          evidence: "none",
          reason:
            "Two registrations of one table row are a clash whether or not the two files match: " +
            "the engine keeps one row under that name and the other is discarded.",
        },
        vanilla: false,
        emergency,
        finding: {
          code: "manifest.data_file.double_registered",
          severity: "error",
          title: `${rowKey} is defined as a ${noun} by ${copies.length} resources`,
          detail:
            `${listLabels(copies.map((copy) => copy.label))} each register a ${type} ` +
            `(${[...new Set(members.map((row) => row.decl.value))].join(", ")}) that defines a ` +
            `${noun} called ${rowKey}. Those files are merged into one server-wide table, so the ` +
            "two rows land on each other and one of them never applies — the vehicle spawns with " +
            "the other pack's values and nothing in the console says so. Two packs both called " +
            "`handling.meta` is the usual shape of this, which is why a checker that compares file " +
            `names sees nothing. Rename the ${noun} in one of them.` +
            (alsoSpawn
              ? ` ${rowKey} is also a spawn-code collision between these same resources, reported ` +
                "separately above: the two are one rename, and the model name in vehicles.meta and " +
                `the ${noun} have to change together or the vehicle spawns with no ${noun} at all.`
              : ""),
          entity: { kind: "manifest", id: `${type} ${rowKey}` },
          ...(emergency ? { tags: ["emergency-impact" as GtaFindingTag] } : {}),
        },
      }),
    );
  }

  if (unreadable.length > 0) {
    skipped.push(
      `Duplicate data_file registrations: ${unreadable.length} registered meta file` +
        `${unreadable.length === 1 ? " was" : "s were"} in the inventory and not read, so nothing ` +
        "they declare was compared against another resource: " +
        `${unreadable.slice(0, 5).join("; ")}${unreadable.length > 5 ? "; and more" : ""}.`,
    );
  }

  return clashes;
}

/**
 * Basenames the convention fixes, which two packs therefore share by following
 * it rather than by claiming anything.
 *
 * This is the guard that keeps rule one from arriving at its own stated failure
 * through the audio door. `AUDIO_WAVEPACK` is a global-name type and
 * `audio_occlusion.dat` is the name FiveM's own filename-to-type table gives
 * that file (FiveM's fxmanifest documentation, tier 1) — every
 * MLO that ships occlusion data ships it under exactly that name, in exactly
 * that declaration. Keying it on its basename reported forty correct interiors
 * as one red finding, which is the sentence `GLOBAL_NAME_DATA_FILE_TYPES` is
 * written against, reached from the other side.
 *
 * The list is the non-`.meta` half of that tier-1 table plus
 * `META_DATA_FILE_KINDS`, and it is deliberately conservative in the safe
 * direction: a distinctive name missing from it costs one finding, while a
 * conventional name missing from it costs the tool its first real server.
 * Sourced, not remembered — every entry is in the table cited above.
 */
const FIXED_CONVENTION_DATA_FILE_NAMES: ReadonlySet<string> = new Set([
  ...Object.keys(META_DATA_FILE_KINDS),
  "audio_occlusion.dat",
  "sounds.dat",
  "popgroups.ymt",
  "popcycle.dat",
  "relationships.dat",
  "content.xml",
  "setup2.xml",
  "dlctext.gxt2",
]);

/**
 * The name the whole server files this declaration under, or `null`.
 *
 * A value only earns a global name when it is distinctive to the pack that
 * wrote it — `audioconfig/dlcambo_game` is the pack's own bank stem, and the
 * server really does file one bank under it. A conventional filename is the
 * opposite: it is the same in every pack by design, so two packs sharing it is
 * evidence of two packs being correct.
 */
function globalNameOf(decl: DataFileDeclaration): string | null {
  const name = normalizeName(baseName(decl.value));
  if (name === "" || FIXED_CONVENTION_DATA_FILE_NAMES.has(name)) return null;
  if (GLOBAL_NAME_DATA_FILE_TYPES.has(decl.type)) return name;
  if (isManifestDeclaredExtension(extensionOf(decl.value))) return name;
  return null;
}

// ─── Building a contest ──────────────────────────────────────────────────────

function copyOf(
  input: GtaResourceInput,
  label: string,
  path: string,
  size: number,
  cfg: CfgIndex,
): ClashCopy {
  const identity = input.identity ?? input.name;
  const name = normalizeName(input.name);
  return {
    resource: input.name,
    identity,
    label,
    origin: input.origin ?? null,
    path,
    size,
    // `null` rather than the position of the `ensure` line, whenever the drop
    // holds a second resource of this name: the line names one of them and
    // nothing here knows which. Printing `#2 in the order` against a copy that
    // may not be the one that starts is a fact asserted about the wrong folder.
    startPosition: cfg.ambiguous.has(name) ? null : (cfg.position.get(name) ?? null),
  };
}

type ClashDraft = {
  kind: ClashKind;
  key: string;
  title: string;
  copies: ClashCopy[];
  cfg: CfgIndex;
  content: ClashContent;
  vanilla: boolean;
  emergency: boolean;
  finding: GtaFinding;
};

/** The end of the start order that wins, per kind. See the header's argument. */
const WINNING_END: Record<ClashKind, "first" | "last" | null> = {
  stream: "last",
  spawn: "first",
  data_file: null,
};

function finishClash(draft: ClashDraft): Clash {
  const winner = resolveWinner(draft.copies, draft.cfg, WINNING_END[draft.kind]);
  const tags = new Set<GtaFindingTag>(draft.finding.tags ?? []);
  if (draft.emergency) tags.add("emergency-impact");
  if (draft.vanilla) tags.add("vanilla");

  const finding: GtaFinding = {
    ...draft.finding,
    detail: `${draft.finding.detail} ${winnerSentence(winner)}`,
    ...(tags.size > 0 ? { tags: [...tags] } : {}),
  };

  return {
    kind: draft.kind,
    key: draft.key,
    title: draft.title,
    copies: draft.copies,
    content: draft.content,
    winner,
    vanilla: draft.vanilla,
    emergency: draft.emergency,
    finding,
  };
}

function winnerSentence(winner: ClashWinner): string {
  const caveats = winner.caveats.length > 0 ? ` ${winner.caveats.join(" ")}` : "";
  return `${winner.reason}${caveats}`;
}

// ─── The analysis ────────────────────────────────────────────────────────────

/**
 * Every contest in a drop, with the winner and the identical-or-different split.
 *
 * Pure and synchronous. It reads no file, opens no archive and fetches nothing —
 * the bytes it reasons about arrive as `options.digests`, which is what makes
 * this testable against a literal object and what keeps a 20 MB model out of the
 * analyzer, exactly as `GtaResourceInput.identicalPairs` already argued.
 *
 * The collision detection is `analyzeResourceSet`'s and the vehicle parsing is
 * `analyzeVehicleMeta`'s — this runs them once and builds on top. That is not a
 * performance note, it is the reason a `<modelName>` inside an XML comment is
 * not counted here: the regex scraper the prior art uses matches inside
 * comments, and the only way not to inherit that is to not have a second parser.
 */
export function findClashes(
  inputs: readonly GtaResourceInput[],
  options: ClashOptions = {},
): ClashReport {
  const resources = (inputs ?? []).filter((input): input is GtaResourceInput => Boolean(input));
  const cfg = indexCfg(options.cfg, resources);
  const skipped: string[] = [];

  const report = analyzeResourceSet(resources, { vanillaNames: options.vanillaNames });

  // The one spelling decision for the whole report, made where every resource is
  // visible at once, so a contest's sentence and the section a reader scrolls to
  // cannot disagree about which `police` is which. Same rule, same module, as
  // `analyzeResourceSet` uses for its own findings.
  const labels = new Map<string, string>(
    disambiguateLabels(
      resources.map((input) => ({
        name: input.name,
        identity: input.identity,
        origin: input.origin,
      })),
      PROSE_LABEL_STYLE,
    ).map((label, index) => [
      normalizeName(resources[index].identity ?? resources[index].name),
      label,
    ]),
  );
  const labelFor = (input: GtaResourceInput) =>
    labels.get(normalizeName(input.identity ?? input.name)) ?? input.name;

  const digests = new Map<string, string | null>();
  for (const entry of options.digests ?? []) {
    if (!entry || typeof entry.identity !== "string" || typeof entry.path !== "string") continue;
    digests.set(copyKey(entry.identity, entry.path), entry.digest ?? null);
  }
  const claims = buildClaimIndex(resources);

  const byIdentity = new Map<string, GtaResourceInput>();
  for (const input of resources) byIdentity.set(normalizeName(input.identity ?? input.name), input);

  // Spawn first, and its result handed to the `data_file` rules rather than
  // recomputed there: the merged vehicle tables contest the same names, and the
  // only way the two sets of cards can be made to agree about which are one
  // problem is for one of them to be able to see the other.
  const spawn = spawnClashes(report, resources, labelFor, cfg);
  const spawnContests = new Map<string, ReadonlySet<string>>(
    spawn.map((clash) => [
      clash.key,
      new Set(clash.copies.map((copy) => normalizeName(copy.identity))),
    ]),
  );

  const clashes: Clash[] = [
    ...streamClashes(report, byIdentity, labelFor, cfg, digests, claims, options.vanillaNames),
    ...spawn,
    ...dataFileClashes(resources, report, labels, cfg, skipped, spawnContests),
  ];

  clashes.sort(
    (a, b) =>
      severityRank(a.finding.severity) - severityRank(b.finding.severity) ||
      Number(b.emergency) - Number(a.emergency) ||
      compareText(a.kind, b.kind) ||
      compareText(a.key, b.key),
  );

  const kept = clashes.slice(0, MAX_CLASHES);
  if (clashes.length > kept.length) {
    skipped.push(
      `Only the first ${MAX_CLASHES.toLocaleString("en-US")} of ` +
        `${clashes.length.toLocaleString("en-US")} collisions are listed. The rest were found and ` +
        "are not shown, so this page is not a complete list. Scan the folder in parts.",
    );
  }

  // The stream collisions `analyzeStreamAssets` wrote are replaced rather than
  // joined, because this module's version of the same finding carries the two
  // sentences that module could not write: which copy wins, and whether the two
  // copies are even different. Keyed on code plus entity so the replacement is
  // exact — `findingKey` includes the title, and the title is one of the things
  // that changes here.
  const replaced = new Set(
    kept
      .filter((clash) => clash.kind === "stream")
      .map((clash) => `${clash.finding.code}\u0000${normalizeName(clash.key)}`),
  );
  const carried = report.findings.filter(
    (finding) =>
      !replaced.has(`${finding.code}\u0000${normalizeName(finding.entity?.id ?? "")}`),
  );

  const findings = sortFindings(
    dedupeFindings([...kept.map((clash) => clash.finding), ...carried]),
  );

  if (!cfg.supplied && kept.length > 0) skipped.push(NO_CFG_SKIP);
  // A drop of one resource is the one input on which nothing in this module can
  // fire: every rule above needs two owners of one name. A clean-looking footer
  // over it is a clean bill of health nobody issued, and the reader's only
  // other signal is a "1" in a totals tile they have to interpret.
  if (resources.length < 2) {
    skipped.push(
      `Every collision check here compares one resource against another, and this drop holds ` +
        `${resources.length === 1 ? "one resource" : "none"}. None of them could fire, so an ` +
        "empty collision list says nothing about this drop. Scan the whole `resources/` folder, " +
        "or the packs you want compared, together.",
    );
  }
  const unhashed = kept.filter(
    (clash) => clash.kind === "stream" && clash.content.verdict === "unknown",
  ).length;
  if (unhashed > 0) {
    skipped.push(
      `Identical-or-different check: ${unhashed} contested name` +
        `${unhashed === 1 ? "" : "s"} went without a byte comparison, so this report does not say ` +
        `whether ${unhashed === 1 ? "its copies are" : "their copies are"} the same file. They are ` +
        "reported as collisions regardless.",
    );
  }

  const allSkipped = [
    ...new Set([...report.skipped, ...skipped, ...(options.hashNotes ?? [])]),
  ];

  return {
    clashes: kept,
    housekeeping: findings.filter((finding) => finding.code === "stream.path.duplicate"),
    findings,
    report: { ...report, findings, skipped: allSkipped },
    cfg: {
      supplied: cfg.supplied,
      order: cfg.order,
      caveats: cfgCaveats(cfg),
    },
    counts: {
      resources: resources.length,
      clashes: kept.length,
      stream: kept.filter((clash) => clash.kind === "stream").length,
      spawn: kept.filter((clash) => clash.kind === "spawn").length,
      dataFile: kept.filter((clash) => clash.kind === "data_file").length,
      identical: kept.filter((clash) => clash.content.verdict === "identical").length,
      different: kept.filter((clash) => clash.content.verdict === "different").length,
      unknownContent: kept.filter((clash) => clash.content.verdict === "unknown").length,
      unknownStream: unhashed,
      resolvedWinners: kept.filter((clash) => clash.winner.resolved).length,
    },
    skipped: allSkipped,
  };
}

function cfgCaveats(cfg: CfgIndex): string[] {
  if (!cfg.supplied) return [];
  const caveats: string[] = [];
  if (cfg.execs > 0) {
    caveats.push(
      `This config \`exec\`s ${cfg.execs} other file${cfg.execs === 1 ? "" : "s"}. Their ensure ` +
        "lines are not in this file and cannot be read from it, so the order below is this file's " +
        "order rather than the server's.",
    );
  }
  if (cfg.categories.size > 0) {
    caveats.push(
      `It starts ${[...cfg.categories].join(", ")} as a unit. A bracket ensure starts every ` +
        "resource in that folder, in the folder's own order, which no config states.",
    );
  }
  if (cfg.repeated.size > 0) {
    caveats.push(
      `${[...cfg.repeated].join(", ")} ${cfg.repeated.size === 1 ? "is" : "are"} ensured more than ` +
        "once. The first line is the one that starts it; the later ones do nothing.",
    );
  }
  caveats.push(...cfg.truncated);
  return caveats;
}

function severityRank(severity: GtaFinding["severity"]): number {
  if (severity === "error") return 0;
  return severity === "warning" ? 1 : 2;
}

// ─── The three kinds ─────────────────────────────────────────────────────────

/**
 * One contest per streamed name two or more resources ship.
 *
 * The severity is where the split earns its keep. A genuine override stays an
 * error — something a pack shipped never appears in game. Two byte-identical
 * copies drop to `info`: one of them still loses the namespace and it changes
 * nothing a player can see, and painting that the same red as a real shadowing
 * is how a reader learns to skip the red. An unknown verdict stays an error,
 * because a check that did not run is not a check that passed.
 */
function streamClashes(
  report: GtaResourceSetReport,
  byIdentity: ReadonlyMap<string, GtaResourceInput>,
  labelFor: (input: GtaResourceInput) => string,
  cfg: CfgIndex,
  digests: Map<string, string | null>,
  claims: ClaimIndex,
  vanillaNames: VanillaAssetNameIndex | undefined,
): Clash[] {
  const clashes: Clash[] = [];
  for (const asset of report.stream) {
    if (distinctIdentities(asset).size < 2) continue;

    const seen = new Set<string>();
    const copies: ClashCopy[] = [];
    for (const occurrence of asset.occurrences) {
      const identity = occurrence.resourceIdentity ?? occurrence.resource;
      const key = normalizeName(identity);
      if (seen.has(key)) continue;
      const input = byIdentity.get(key);
      if (!input) continue;
      seen.add(key);
      copies.push(copyOf(input, labelFor(input), occurrence.path, occurrence.size, cfg));
    }
    if (copies.length < 2) continue;

    const content = resolveContent(copies, digests, claims);
    const emergency = isEmergencyVehicleName(stemOf(stripHighLod(asset.basename))).emergency;
    const severity = content.verdict === "identical" ? "info" : "error";
    const vanilla = asset.vanilla === true || isVanillaName(asset.basename, vanillaNames);

    clashes.push(
      finishClash({
        kind: "stream",
        key: asset.basename,
        title: asset.basename,
        copies,
        cfg,
        content,
        vanilla,
        emergency,
        finding: {
          code: "stream.basename.duplicate",
          severity,
          title:
            content.verdict === "identical"
              ? `${asset.basename} is streamed by ${copies.length} resources, byte for byte the same file`
              : `${asset.basename} is streamed by ${copies.length} resources`,
          detail:
            `${listLabels(copies.map((copy) => copy.label))} each ship an asset called ` +
            `${asset.basename}. The stream namespace is flat — there are no folders in it — so the ` +
            `server loads exactly one of them and nothing is written to the console when it does. ` +
            `${content.reason}` +
            (vanilla
              ? ` The base game also owns ${asset.basename}, so whichever copy wins is replacing ` +
                "the stock asset everywhere as well as beating the other resource."
              : ""),
          file: copies[0].path,
          entity: { kind: "stream_asset", id: asset.basename },
        },
      }),
    );
  }
  return clashes;
}

/**
 * Whether the base game owns this exact name, or `false` when we could not tell.
 *
 * `collectStreamAssets` leaves `StreamAsset.vanilla` false and
 * `analyzeStreamAssets` decides the question without writing its answer back, so
 * a consumer of `report.stream` has to ask the index again rather than read a
 * field that was never filled in. A failing index answers `false` here rather
 * than throwing, and that is safe precisely because `false` is not a claim: an
 * override that goes unmarked loses a sentence of context in a finding that
 * fires either way, and the report already carries `analyzeStreamAssets`'s own
 * line in `skipped` saying the base-game check could not run.
 */
function isVanillaName(basename: string, index: VanillaAssetNameIndex | undefined): boolean {
  if (!index || typeof index.has !== "function") return false;
  try {
    return index.has(basename) === true;
  } catch {
    return false;
  }
}

/**
 * One contest per spawn code two or more resources declare.
 *
 * Built from `VehicleEntity`, which came out of `parseVehiclesMeta` — a real XML
 * parser over a real document. That is claim five of the brief and it is had for
 * free by not writing a second reader: a `<modelName>` inside an XML comment is
 * not an element, so it is not in the document, so it cannot be counted. The
 * regex scraper the prior art uses matches it, and also drops every model name
 * containing a hyphen, a dot or a non-ASCII character, and never opens
 * `carvariations.meta` at all — `analyzeVehicleMeta` reads that too, which is
 * where `vehicle.variation.orphan` comes from in the same report.
 *
 * Whether each meta is *registered* is not re-litigated here. A `vehicles.meta`
 * the manifest never declares defines nothing at runtime, and that is already
 * `manifest.meta.not_registered`, sitting in the same findings list — a second
 * opinion about it here would be a second thing to keep in step.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * KNOWN DIVERGENCE FROM `spawnIndex.ts`, WHICH IS DELIBERATE AND IS NOT DRIFT
 *
 * `buildSpawnIndex` groups spawn codes across packs too, and emits the same
 * `vehicle.model.duplicate` code. It is not reused here and the two do not take
 * the same input, so they can disagree — say so rather than let a reader assume
 * they cannot.
 *
 * `SpawnPack.codes` carries two tiers: codes read out of a `vehicles.meta`, and
 * codes GUESSED from the names of files in `stream/` when a pack has no meta to
 * read. Spawn Index exists to enumerate a pack's codes and a guess is worth
 * showing there, badged as a guess. A collision report is a different promise:
 * every row here is a fault somebody is being asked to fix, and "these two packs
 * both declare `police`" inferred from a file called `police.yft` is a guess
 * wearing an error's badge. So the contests here are built from `VehicleEntity`
 * — the meta tier only — and a manifest-less pack shipping `stream/police.yft`
 * beside a pack that declares `police` is reported here as the `stream` clash on
 * `police.yft` that it provably is, rather than as a spawn-code clash that may
 * not be one.
 *
 * The cost of reusing `buildSpawnIndex` instead is real and is not only the
 * false positive: it re-reads every resource — tier selection, filename
 * guessing, per-pack vanilla checks — to arrive at a meta tier that
 * `analyzeResourceSet` has already produced and that this function is already
 * holding. If a third consumer ever wants one grouping, the place for it is a
 * shared function over `VehicleEntity`, not one tool importing the other's
 * pipeline.
 */
function spawnClashes(
  report: GtaResourceSetReport,
  inputs: readonly GtaResourceInput[],
  labelFor: (input: GtaResourceInput) => string,
  cfg: CfgIndex,
): Clash[] {
  type SpawnRow = { input: GtaResourceInput; file: string; displayed: string; emergency: boolean };
  const byModel = new Map<string, SpawnRow[]>();

  report.resources.forEach((resource, index) => {
    const input = inputs[index];
    if (!input) return;
    for (const vehicle of resource.vehicles) {
      const model = normalizeName(vehicle.model);
      if (model === "") continue;
      byModel.set(model, [
        ...(byModel.get(model) ?? []),
        {
          input,
          file: vehicle.span.file ?? "vehicles.meta",
          displayed: vehicle.modelRaw || vehicle.model,
          emergency: vehicle.emergency === true,
        },
      ]);
    }
  });

  const clashes: Clash[] = [];
  for (const [model, rows] of byModel) {
    const owners = new Map<string, SpawnRow>();
    for (const row of rows) {
      const key = normalizeName(row.input.identity ?? row.input.name);
      if (!owners.has(key)) owners.set(key, row);
    }
    if (owners.size < 2) continue;

    const members = [...owners.values()];
    const copies = members.map((row) => copyOf(row.input, labelFor(row.input), row.file, 0, cfg));
    const emergency = members.some((row) => row.emergency) || isEmergencyVehicleName(model).emergency;

    clashes.push(
      finishClash({
        kind: "spawn",
        key: model,
        title: members[0].displayed,
        copies,
        cfg,
        content: {
          verdict: "unknown",
          evidence: "none",
          reason:
            "A spawn code clash is about a name, not about bytes: two packs can ship entirely " +
            "different cars under one code and the game still keeps only one of them.",
        },
        vanilla: false,
        emergency,
        finding: {
          code: "vehicle.model.duplicate",
          severity: "error",
          title: `${members[0].displayed} is a spawn code in ${copies.length} resources`,
          detail:
            `${listLabels(copies.map((copy) => copy.label))} each declare ${model} in a ` +
            "vehicles.meta. The game registers one model per name and discards the rest, so half " +
            "of what the server paid for never appears and nothing in the console says so. " +
            "Neither pack is wrong on its own, which is why only a view across the whole drop can " +
            "see it. Rename the code in one of them — in vehicles.meta and on the stream files " +
            "together.",
          file: copies[0].path,
          entity: { kind: "vehicle", id: model },
        },
      }),
    );
  }
  return clashes;
}
